import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { Loader03, StarFill, StarLine } from 'lib/lucky-x-icons-2';
import { useUserInfo } from 'lib/user';
import Modal from '@/components/PageElement/Modal';
import { useToggle } from 'ahooks';
import { trackProject } from 'api/project/project_track';
import { successMsg } from '@/components/PageElement/SuccessMessage';
import { errMsg } from '@/components/PageElement/ErrorMessage';
import { untrackProject } from 'api/project/project_untrack';
import EmailInput from '@/components/AccountSetting/Notify/EmailInput';
import { CheckLine } from 'lib/lucky-x-icons';
import { setNotRemindMint } from 'api/user_profile/user_profile_not_remind_mint_again';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { subscribeNotification } from 'api/user_profile/user_profile_subscribe_notification';
import {
  setCurSubscribedType,
  setIsNotRemindMint,
  setIsUpdateRemindMint,
  setIsUpdateSubcribe,
} from 'store/features/userSlice';
import { getProjectDetail } from 'api/project/project_detail';
import { sendTrackedMessage } from 'store/features/calendarSlice';
import { useWeb3Modal } from '@/components/Web3Modal';
import Icon_Stroke from 'assets/icons/Icon_Stroke.png';

interface ITrackProps {
  id: number;
  mintDate: number | undefined;
  isTracked: boolean | undefined;
  largeIcon?: boolean;
  calendarIcon?: boolean;
  trackAmount?: number;
}

const Track: React.FC<ITrackProps> = ({
  id,
  mintDate,
  isTracked,
  largeIcon,
  calendarIcon,
  trackAmount,
}) => {
  const dispatch = useAppDispatch();
  const { subscriptions, not_remind_mint, isLogin } = useUserInfo();
  const [loading, setLoading] = useState(false);
  const [loadingRemind, setLoadingRemind] = useState(false);
  const [loadingConfirm, setLoadingConfirm] = useState(false);
  const [openNote, { setRight: setOpenNote, setLeft: setCloseNote }] =
    useToggle();
  const {
    inputEmail,
    validEmail,
    curSubscribedType,
    isUpdateSubcribe,
    isNotRemindMint,
    isUpdateRemindMint,
  } = useAppSelector((state) => state.user);
  const [curTrackStatus, setCurTrackStatus] = useState<boolean | undefined>(
    isTracked
  );
  const [curTrackAmount, setCurTrackAmount] = useState<number | undefined>(
    trackAmount
  );
  const { toggleWeb3Modal } = useWeb3Modal();

  useEffect(() => {
    if (!!localStorage.getItem('openNotRemind')) {
      setOpenNote();
    }
  }, []);

  useEffect(() => {
    if (!isNotRemindMint) {
      dispatch(setIsNotRemindMint(not_remind_mint));
    }
    if (
      !!subscriptions &&
      subscriptions?.length !== 0 &&
      curSubscribedType?.length === 0 &&
      !isUpdateSubcribe
    ) {
      dispatch(setCurSubscribedType(subscriptions));
    }
    if (!isUpdateSubcribe && !isUpdateRemindMint) {
      setCurTrackStatus(isTracked);
      setCurTrackAmount(trackAmount);
    }
  }, [
    curSubscribedType?.length,
    dispatch,
    isNotRemindMint,
    isTracked,
    isUpdateRemindMint,
    isUpdateSubcribe,
    not_remind_mint,
    subscriptions,
    trackAmount,
  ]);

  const subcribedType = useCallback(
    (type: string) => {
      return (
        !!curSubscribedType &&
        curSubscribedType?.findIndex((item) => item === type) !== -1
      );
    },
    [curSubscribedType]
  );

  const toTrackProject = useCallback(async () => {
    setLoading(true);
    const res = await trackProject(id);
    setLoading(false);
    if (res?.code === 0) {
      successMsg('Tracking successful');
      const res1 = await getProjectDetail(id);
      setCurTrackStatus(res1?.project_vo?.is_tracked);
      setCurTrackAmount(res1?.project_vo?.track_amount);
      if (!!mintDate && !subcribedType('PROJECT_MINT') && !isNotRemindMint) {
        setOpenNote();
        localStorage.setItem('openNotRemind', new Date().toString());
      }
      dispatch(sendTrackedMessage(`${id}-add-${Date.now()}`));
    } else {
      errMsg(res?.msg);
    }
  }, [id, mintDate, subcribedType, isNotRemindMint, dispatch, setOpenNote]);

  const toUntrackProject = useCallback(async () => {
    setLoading(true);
    const res = await untrackProject(id);
    setLoading(false);
    if (res?.code === 0) {
      successMsg('Untrack successful');
      const res1 = await getProjectDetail(id);
      setCurTrackStatus(res1?.project_vo?.is_tracked);
      setCurTrackAmount(res1?.project_vo?.track_amount);
      dispatch(sendTrackedMessage(`${id}-un-${Date.now()}`));
    } else {
      errMsg(res?.msg);
    }
  }, [id, dispatch]);

  const toSetNotRemindMint = useCallback(async () => {
    setLoadingRemind(true);
    const res = await setNotRemindMint(!isNotRemindMint);
    setLoadingRemind(false);
    if (res?.code === 0) {
      if (!isNotRemindMint) {
        successMsg('We will not remind you again.');
      }
      dispatch(setIsNotRemindMint(!isNotRemindMint));
      dispatch(setIsUpdateRemindMint(true));
    } else {
      errMsg(res?.msg);
    }
  }, [dispatch, isNotRemindMint]);

  const handleConfirm = useCallback(async () => {
    if (inputEmail === '') {
      errMsg("Email can't be empty");
      return;
    } else if (validEmail === -1) {
      errMsg('Invalid email address');
      return;
    } else {
      setLoadingConfirm(true);
      const res = await subscribeNotification({
        raffle_win: Number(subcribedType('RAFFLE_WIN')),
        project_mint: Number(!subcribedType('PROJECT_MINT')),
      });
      setLoadingConfirm(false);
      if (res?.code === 0) {
        successMsg('Notification updated successfully');
        dispatch(setIsUpdateSubcribe(true));
        dispatch(
          setCurSubscribedType(curSubscribedType?.concat('PROJECT_MINT'))
        );
        setCloseNote();
        localStorage.removeItem('openNotRemind');
      } else {
        errMsg(res?.msg);
      }
    }
  }, [
    curSubscribedType,
    dispatch,
    inputEmail,
    setCloseNote,
    subcribedType,
    validEmail,
  ]);

  const iconSize = useMemo(() => {
    if (largeIcon) {
      return 32;
    } else if (calendarIcon) {
      return 16;
    } else {
      return 20;
    }
  }, [calendarIcon, largeIcon]);

  return (
    <>
      <div
        className={classNames(
          'flex flex-col items-center justify-center gap-1',
          {
            'w-[60px]': largeIcon,
          }
        )}
      >
        <div
          className={classNames(
            ' group relative flex cursor-pointer items-center justify-center rounded-full',
            {
              'h-8 w-8 select-none bg-neutral-100 opacity-50 backdrop-blur hover:bg-neutral-400 hover:opacity-100':
                !largeIcon && !calendarIcon,
            }
          )}
        >
          {loading ? (
            <Loader03
              size={iconSize}
              className="animate-spinb text-neutral-100"
            />
          ) : (
            <div>
              {!curTrackStatus ? (
                <StarLine
                  size={iconSize}
                  className={classNames(' group-hover:text-neutral-100 ', {
                    'text-neutral-800': calendarIcon,
                    'text-neutral-400': !calendarIcon,
                  })}
                  onClick={() => {
                    if (!isLogin) {
                      toggleWeb3Modal();
                    } else {
                      toTrackProject();
                    }
                  }}
                />
              ) : (
                <div>
                  <img
                    src={Icon_Stroke.src}
                    className={classNames('cursor-pointer', {
                      'w-3': calendarIcon,
                      'w-6': largeIcon,
                      'w-4': !largeIcon && !calendarIcon,
                    })}
                    alt=""
                    onClick={() => {
                      if (!isLogin) {
                        toggleWeb3Modal();
                      } else {
                        toUntrackProject();
                      }
                    }}
                  />
                </div>
              )}
            </div>
          )}

          <div
            className={classNames(
              ' absolute  z-20  hidden w-[110px] rounded-lg border-2 border-neutral-700 bg-neutral-800 px-2 py-3 text-left text-sm font-normal  text-neutral-200 lg:group-hover:block',
              {
                '-left-15 -bottom-14': !largeIcon && !calendarIcon,
                '-left-15 -top-14': largeIcon,
                '-top-1 right-6': calendarIcon,
              }
            )}
          >
            Track project
          </div>
        </div>
        {(largeIcon || calendarIcon) && (
          <div
            className={classNames(' font-normal ', {
              'text-[10px] leading-3 text-neutral-800': calendarIcon,
              'text-sm text-neutral-100': largeIcon,
            })}
          >
            {curTrackAmount}
          </div>
        )}
      </div>
      <Modal
        open={openNote}
        handleConfirm={handleConfirm}
        handleCancel={() => {
          setCloseNote();
          localStorage.removeItem('openNotRemind');
        }}
        title="Email notification"
        loadingConfirm={loadingConfirm}
      >
        <div>
          <div className="mb-5">
            You can set up an email address to receive mint alerts for tracked
            projects.
          </div>
          <EmailInput modalBg={true} />
          <div className="flex items-center justify-end gap-2">
            {loadingRemind ? (
              <Loader03 size={16} className="animate-spin text-neutral-100" />
            ) : (
              <div
                className={classNames(
                  'active:buttonShadow h-4 w-4  cursor-pointer rounded  border active:border-primary-500',
                  {
                    'border-neutral-600 hover:border-neutral-100':
                      !isNotRemindMint,
                    'flex items-center justify-center border-none bg-primary-500 hover:bg-primary-600':
                      isNotRemindMint,
                  }
                )}
                onClick={toSetNotRemindMint}
              >
                {isNotRemindMint && (
                  <CheckLine size={14} className="text-neutral-100" />
                )}
              </div>
            )}
            <div>Don&apos;t remind me again</div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default Track;
